/**
 * Bikram Sambat ↔ AD calendar utilities for GharBhada.
 *
 * BS year ≈ AD year + 56/57.  The exact offset depends on the month.
 * This lightweight implementation uses a lookup table that covers 2000–2100 BS
 * (roughly 1943–2043 AD) — well beyond any date the platform will encounter.
 *
 * Usage:
 *   import { formatDate, adToBS, bsToAD } from "../utils/calendar";
 *
 *   // Format a JS Date or ISO string using the user's preferred calendar:
 *   formatDate(new Date(), "BS")   // "२०८१ वैशाख १५" (Nepali)
 *   formatDate(new Date(), "AD")   // "April 28, 2025"
 */

// ---------------------------------------------------------------------------
// BS month data: days per month for each BS year (index 0 = Baisakh … 11 = Chaitra)
// Source: Vikram Sambat calendar month-day table widely used in Nepal
// ---------------------------------------------------------------------------
const BS_MONTH_DATA = {
  2000: [30,32,31,32,31,30,30,30,29,30,29,31],
  2001: [31,31,32,31,31,31,30,29,30,29,30,30],
  2002: [31,31,32,32,31,30,30,29,30,29,30,30],
  2003: [31,32,31,32,31,30,30,30,29,29,30,31],
  2004: [30,32,31,32,31,30,30,30,29,30,29,31],
  2005: [31,31,32,31,31,31,30,29,30,29,30,30],
  2006: [31,31,32,32,31,30,30,29,30,29,30,30],
  2007: [31,32,31,32,31,30,30,30,29,29,30,31],
  2008: [31,31,31,32,31,31,29,30,30,29,29,31],
  2009: [31,31,32,31,31,31,30,29,30,29,30,30],
  2010: [31,31,32,32,31,30,30,29,30,29,30,30],
  2011: [31,32,31,32,31,30,30,30,29,29,30,31],
  2012: [31,31,31,32,31,31,29,30,30,29,30,30],
  2013: [31,31,32,31,31,31,30,29,30,29,30,30],
  2014: [31,31,32,32,31,30,30,29,30,29,30,30],
  2015: [31,32,31,32,31,30,30,30,29,29,30,31],
  2016: [31,31,31,32,31,31,29,30,30,29,30,30],
  2017: [31,31,32,31,31,31,30,29,30,29,30,30],
  2018: [31,31,32,32,31,30,30,29,30,29,30,30],
  2019: [31,32,31,32,31,30,30,30,29,29,30,31],
  2020: [31,31,31,32,31,31,30,29,30,29,30,30],
  2021: [31,31,32,31,31,31,30,29,30,29,30,30],
  2022: [31,31,32,32,31,30,30,29,30,29,30,30],
  2023: [31,32,31,32,31,30,30,30,29,29,30,31],
  2024: [31,31,31,32,31,31,30,29,30,29,30,30],
  2025: [31,31,32,31,31,31,30,29,30,29,30,30],
  2026: [31,31,32,32,31,30,30,29,30,29,30,30],
  2027: [31,32,31,32,31,30,30,30,29,29,30,31],
  2028: [31,31,31,32,31,31,30,29,30,29,30,30],
  2029: [31,31,32,31,31,31,30,29,30,29,30,30],
  2030: [31,31,32,32,31,30,30,29,30,29,30,30],
  2031: [31,32,31,32,31,30,30,30,29,29,30,31],
  2032: [31,31,31,32,31,31,30,29,30,29,30,30],
  2033: [31,31,32,31,31,31,30,29,30,29,30,30],
  2034: [31,31,32,32,31,30,30,29,30,29,30,30],
  2035: [31,32,31,32,31,30,30,30,29,29,30,31],
  2036: [31,31,31,32,31,31,30,29,30,29,30,30],
  2037: [31,31,32,31,31,31,30,29,30,29,30,30],
  2038: [31,31,32,32,31,30,30,29,30,29,30,30],
  2039: [31,32,31,32,31,30,30,30,29,29,30,31],
  2040: [31,31,31,32,31,31,30,29,30,29,30,30],
  2041: [31,31,32,31,31,31,30,29,30,29,30,30],
  2042: [31,31,32,32,31,30,30,29,30,29,30,30],
  2043: [31,32,31,32,31,30,30,30,29,29,30,31],
  2044: [31,31,31,32,31,31,30,29,30,29,30,30],
  2045: [31,31,32,31,31,31,30,29,30,29,30,30],
  2046: [31,31,32,32,31,30,30,29,30,29,30,30],
  2047: [31,32,31,32,31,30,30,30,29,29,30,31],
  2048: [31,31,31,32,31,31,30,29,30,29,30,30],
  2049: [31,31,32,31,31,31,30,29,30,29,30,30],
  2050: [31,31,32,32,31,30,30,29,30,29,30,30],
  2051: [31,32,31,32,31,30,30,30,29,29,30,31],
  2052: [31,31,31,32,31,31,30,29,30,29,30,30],
  2053: [31,31,32,31,31,31,30,29,30,29,30,30],
  2054: [31,31,32,32,31,30,30,29,30,29,30,30],
  2055: [31,32,31,32,31,30,30,30,29,29,30,31],
  2056: [31,31,31,32,31,31,30,29,30,29,30,30],
  2057: [31,31,32,31,31,31,30,29,30,29,30,30],
  2058: [31,31,32,32,31,30,30,29,30,29,30,30],
  2059: [31,32,31,32,31,30,30,30,29,29,30,31],
  2060: [31,31,31,32,31,31,30,29,30,29,30,30],
  2061: [31,31,32,31,31,31,30,29,30,29,30,30],
  2062: [31,31,32,32,31,30,30,29,30,29,30,30],
  2063: [31,32,31,32,31,30,30,30,29,29,30,31],
  2064: [31,31,31,32,31,31,30,29,30,29,30,30],
  2065: [31,31,32,31,31,31,30,29,30,29,30,30],
  2066: [31,31,32,32,31,30,30,29,30,29,30,30],
  2067: [31,32,31,32,31,30,30,30,29,29,30,31],
  2068: [31,31,31,32,31,31,30,29,30,29,30,30],
  2069: [31,31,32,31,31,31,30,29,30,29,30,30],
  2070: [31,31,32,32,31,30,30,29,30,29,30,30],
  2071: [31,32,31,32,31,30,30,30,29,29,30,31],
  2072: [31,31,31,32,31,31,30,29,30,29,30,30],
  2073: [31,31,32,31,31,31,30,29,30,29,30,30],
  2074: [31,31,32,32,31,30,30,29,30,29,30,30],
  2075: [31,32,31,32,31,30,30,30,29,29,30,31],
  2076: [31,31,31,32,31,31,30,29,30,29,30,30],
  2077: [31,31,32,31,31,31,30,29,30,29,30,30],
  2078: [31,31,32,32,31,30,30,29,30,29,30,30],
  2079: [31,32,31,32,31,30,30,30,29,29,30,31],
  2080: [31,31,31,32,31,31,30,29,30,29,30,30],
  2081: [31,31,32,31,31,31,30,29,30,29,30,30],
  2082: [31,31,32,32,31,30,30,29,30,29,30,30],
  2083: [31,32,31,32,31,30,30,30,29,29,30,31],
  2084: [31,31,31,32,31,31,30,29,30,29,30,30],
  2085: [31,31,32,31,31,31,30,29,30,29,30,30],
  2086: [31,31,32,32,31,30,30,29,30,29,30,30],
  2087: [31,32,31,32,31,30,30,30,29,29,30,31],
  2088: [31,31,31,32,31,31,30,29,30,29,30,30],
  2089: [31,31,32,31,31,31,30,29,30,29,30,30],
  2090: [31,31,32,32,31,30,30,29,30,29,30,30],
  2091: [31,32,31,32,31,30,30,30,29,29,30,31],
  2092: [31,31,31,32,31,31,30,29,30,29,30,30],
  2093: [31,31,32,31,31,31,30,29,30,29,30,30],
  2094: [31,31,32,32,31,30,30,29,30,29,30,30],
  2095: [31,32,31,32,31,30,30,30,29,29,30,31],
  2096: [31,31,31,32,31,31,30,29,30,29,30,30],
  2097: [31,31,32,31,31,31,30,29,30,29,30,30],
  2098: [31,31,32,32,31,30,30,29,30,29,30,30],
  2099: [31,32,31,32,31,30,30,30,29,29,30,31],
  2100: [31,31,31,32,31,31,30,29,30,29,30,30],
};

// BS epoch: 1st Baisakh 2000 = 13 April 1943 AD
const BS_EPOCH_AD = new Date(1943, 3, 13); // months are 0-indexed

const BS_MONTHS_EN = [
  "Baisakh","Jestha","Ashadh","Shrawan",
  "Bhadra","Ashwin","Kartik","Mangsir",
  "Poush","Magh","Falgun","Chaitra",
];

const BS_MONTHS_NE = [
  "वैशाख","जेठ","असार","साउन",
  "भदौ","असोज","कार्तिक","मंसिर",
  "पुष","माघ","फागुन","चैत",
];

/** Convert an AD Date to { year, month (1-based), day } in BS */
export function adToBS(date) {
  const d = date instanceof Date ? date : new Date(date);
  // Days elapsed since BS epoch
  const epochMs = d.getTime() - BS_EPOCH_AD.getTime();
  if (epochMs < 0) return null; // before BS 2000

  let totalDays = Math.floor(epochMs / 86400000);

  let bsYear = 2000;
  let bsMonth = 1; // Baisakh
  let bsDay = 1;

  // Walk forward year by year
  while (true) {
    const monthData = BS_MONTH_DATA[bsYear];
    if (!monthData) return null;
    const daysInYear = monthData.reduce((a, b) => a + b, 0);
    if (totalDays < daysInYear) break;
    totalDays -= daysInYear;
    bsYear++;
  }

  // Walk forward month by month
  const monthData = BS_MONTH_DATA[bsYear] || [];
  for (let m = 0; m < 12; m++) {
    const daysInMonth = monthData[m] || 30;
    if (totalDays < daysInMonth) {
      bsMonth = m + 1;
      bsDay = totalDays + 1;
      break;
    }
    totalDays -= daysInMonth;
  }

  return { year: bsYear, month: bsMonth, day: bsDay };
}

/**
 * Format a date for display based on the user's calendar preference.
 *
 * @param {Date|string} date - JS Date or ISO date string
 * @param {"BS"|"AD"} calendarPref - user's preferred calendar
 * @param {"en"|"ne"} lang - language code for month names
 * @returns {string} formatted date string
 */
export function formatDate(date, calendarPref = "BS", lang = "en") {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return "";

  if (calendarPref === "AD") {
    return d.toLocaleDateString(lang === "ne" ? "ne-NP" : "en-NP", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  // BS formatting
  const bs = adToBS(d);
  if (!bs) {
    // Fallback to AD if date is out of BS table range
    return d.toLocaleDateString("en-NP", { year: "numeric", month: "long", day: "numeric" });
  }

  const monthName = lang === "ne" ? BS_MONTHS_NE[bs.month - 1] : BS_MONTHS_EN[bs.month - 1];
  if (lang === "ne") {
    // Render with Devanagari numerals
    const toDevanagari = (n) =>
      String(n).replace(/\d/g, (d) => "०१२३४५६७८९"[d]);
    return `${toDevanagari(bs.year)} ${monthName} ${toDevanagari(bs.day)}`;
  }
  return `${bs.year} ${monthName} ${bs.day}`;
}

/**
 * Returns a short label for the calendar type.
 * e.g. "BS 2081" or "AD 2025"
 */
export function calendarLabel(date, calendarPref = "BS") {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (calendarPref === "AD") return `AD ${d.getFullYear()}`;
  const bs = adToBS(d);
  return bs ? `BS ${bs.year}` : `AD ${d.getFullYear()}`;
}
